// @perlab/cross-tab-idb — idb-keyval adapter implementing the KVStore
// interface from @perlab/cross-tab.
//
// Per spec §3.1 (28_Parallel_Work_Architecture_Tier1_V3.md), the 5 logical
// stores are backed by 5 separate IDB object stores (resolves MED N12
// transaction contention). Each store gets its own `UseStore` handle from
// idb-keyval at construction; KVStore methods route to the right handle by
// store-name lookup.
//
// Written by TL on 2026-05-28 as the IdbKvStore-write-never-landed unblock
// for cross-tab-scaffold agent (writes to this path returned success but
// did not reach disk; activity.jsonl shows no block entry — bytes simply
// never sent). Spec compliance verified against kv-store.ts contract.

import {
  createStore,
  get as idbGet,
  set as idbSet,
  del as idbDel,
  clear as idbClear,
  entries as idbEntries,
  type UseStore,
} from 'idb-keyval';
import type { KVStore, KVStoreName } from '@perlab/cross-tab';

import {
  ALL_STORES,
  DB_NAME,
} from './stores.js';

export {
  STORE_KV,
  STORE_NOTIFICATIONS,
  STORE_RECENTS,
  STORE_REPLAY,
  STORE_PINNED,
  ALL_STORES,
  DB_NAME,
  DB_VERSION,
} from './stores.js';

/**
 * idb-keyval-backed KVStore. One IDB DATABASE per KVStoreName per spec §3.1
 * (resolves MED N12 — stronger isolation than one DB with multiple object
 * stores; each store has its own transaction scope by virtue of being its
 * own database).
 *
 * Construction is synchronous: each `createStore(dbName, storeName)` returns
 * a `UseStore` handle that lazily opens the DB on first use.
 *
 * IMPLEMENTATION NOTE — `idb-keyval` multi-store gotcha:
 * `createStore(SAME_DB, storeName)` called multiple times for different
 * storeNames against the SAME database name does NOT create multiple object
 * stores — the upgrade callback only runs once per DB version, so only the
 * first storeName gets created and subsequent calls throw NotFoundError on
 * use. Workaround per idb-keyval docs: give each logical store its own DB
 * name. We prefix `DB_NAME` with the store name → 5 IDB databases
 * (`perlab.shell.kv`, `perlab.shell.notifications`, etc.). Each is small
 * (single object store + single keyspace) and the browser handles
 * many-DBs-per-origin trivially. This pattern also doubles as physical
 * MED N12 isolation: cross-store contention is impossible at the IDB layer.
 *
 * Construction asserts ALL_STORES is exhaustive vs KVStoreName at compile
 * time via the `Record<KVStoreName, UseStore>` type.
 */
export class IdbKvStore implements KVStore {
  private readonly useStores: Record<KVStoreName, UseStore>;

  constructor() {
    const handles = {} as Record<KVStoreName, UseStore>;
    for (const name of ALL_STORES) {
      // Per-store DB name — see IMPLEMENTATION NOTE on the class.
      handles[name] = createStore(`${DB_NAME}.${name}`, name);
    }
    this.useStores = handles;
  }

  private handle(store: KVStoreName): UseStore {
    return this.useStores[store];
  }

  async get<T = unknown>(
    store: KVStoreName,
    key: IDBValidKey,
  ): Promise<T | undefined> {
    return (await idbGet(key, this.handle(store))) as T | undefined;
  }

  async put<T = unknown>(
    store: KVStoreName,
    key: IDBValidKey,
    value: T,
  ): Promise<void> {
    await idbSet(key, value, this.handle(store));
  }

  async getAll<T = unknown>(
    store: KVStoreName,
  ): Promise<Array<{ key: IDBValidKey; value: T }>> {
    const rows = await idbEntries<IDBValidKey, T>(this.handle(store));
    return rows.map(([key, value]) => ({ key, value }));
  }

  async delete(store: KVStoreName, key: IDBValidKey): Promise<void> {
    await idbDel(key, this.handle(store));
  }

  async clear(store: KVStoreName): Promise<void> {
    await idbClear(this.handle(store));
  }
}
